import { useCallback, useRef } from "react";
import OptimizedNavigationControl from "../components/Maps/utils/OptimizedNavigationControl";
import PitchToggle from "../components/Maps/utils/PitchToggle";

/**
 * Shared setup for the Mapbox navigation + pitch-toggle controls used by the
 * device and geofence overview maps. Keeps control instances in refs so they
 * are added once and removed on unmount.
 *
 * @param {Object} params
 * @param {number} params.pitch - Current pitch value
 * @param {Function} params.debounce - Debounce helper from usePerformanceOptimizations
 * @returns {{ attachControls: (map: Object) => void, cleanupControls: (map: Object) => void }}
 */
export function useMapControlSetup({ pitch, debounce }) {
  const navControlRef = useRef(null);
  const pitchToggleControlRef = useRef(null);

  const attachControls = useCallback(
    (map) => {
      if (!map) return;

      // Optimized navigation control with passive event listeners
      if (!navControlRef.current) {
        navControlRef.current = new OptimizedNavigationControl();
        map.addControl(navControlRef.current, "top-left");
      }

      if (!pitchToggleControlRef.current) {
        pitchToggleControlRef.current = new PitchToggle({ pitch });
      }
      if (!map.hasControl(pitchToggleControlRef.current)) {
        map.addControl(pitchToggleControlRef.current, "top-left");
      }

      // Debounced pitch setting
      const debouncedPitchSet = debounce(
        () => {
          if (typeof pitch === "number") {
            map.setPitch(pitch);
          }
        },
        150,
        "pitchSet"
      );

      debouncedPitchSet();
    },
    [pitch, debounce]
  );

  const cleanupControls = useCallback((map) => {
    [navControlRef.current, pitchToggleControlRef.current].forEach(
      (control) => {
        if (map && control && map.hasControl(control)) {
          map.removeControl(control);
        }
      }
    );
  }, []);

  return { attachControls, cleanupControls };
}
